import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Card, Box, Slider,
    IconButton, Select, MenuItem, CircularProgress,
    Checkbox, Stack, Link, TextField, Divider, Alert, Chip, useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AddCircleOutline, ArrowBack, CheckCircle, TrendingUp, TrendingDown, Add } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_SERVICE_BACKEND, HELP_URL } from 'src/config';
import { useLocation, useNavigate } from 'react-router';
import { formatToLocalDate } from 'src/utils/format-to-local-date';
import HelperLink from 'src/components/HelperLink';

const AddonModal = ({ addonsData, userDetailsRecurring = [], getUserInfo, state, userDetails }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sliders, setSliders] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCard, setIsLoadingCard] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [isCouponApplied, setIsCouponApplied] = useState(false);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation()



    const userEmail = sessionStorage.getItem('userEmail');

    const handleToggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(sid => sid !== id)
                : [...prev, id]
        );
    };

    const handleOpen = () => {
        setOpen(true);
        setStep(1);
        setSelectedIds([]);
        setSliders({});
        setPaymentMethods([]);
        setSelectedPaymentMethod('');
        setCouponCode('');
        setIsCouponApplied(false);
        setPreviewData(null);
    };

    const handleClose = () => {
        setOpen(false);
        setStep(1);
        setSelectedIds([]);
        setSliders({});
        setPaymentMethods([]);
        setSelectedPaymentMethod('');
        setCouponCode('');
        setIsCouponApplied(false);
        setPreviewData(null);
    };


    useEffect(() => {
        if (state?.manageAddon) {
            handleOpen()
            // Clear the state from history to prevent it staying on refresh:
            navigate(location.pathname, { manageAddon: false });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        if (step === 2) {
            const newSliders = {};
            selectedIds.forEach(id => {
                const addon = addonsData.find(a => a.id === id);
                const addonName = addon.metadata?.name;
                const matchedSub = userDetailsRecurring.find(sub => sub.planId === addonName && !sub.cancelled);
                const isMultichannel = addonName === 'MULTICHANNEL_ACCOUNTS';
                const isVoiceMins = addonName === 'AI_VOICE_AGENT_MINS';
                const isPhoneNumbers = addonName === 'PHONE_NUMBERS';

                const chunkSize = addon?.default_price?.transform_quantity?.divide_by
                    ? parseInt(addon.default_price.transform_quantity.divide_by)
                    : 1;

                let prefillValue;
                if (matchedSub) {
                    prefillValue = matchedSub.quantity;
                } else if (isVoiceMins || isPhoneNumbers) {
                    prefillValue = 300;
                } else if (isMultichannel) {
                    prefillValue = 1;
                } else {
                    prefillValue = chunkSize;
                }

                newSliders[id] = prefillValue;
            });
            setSliders(newSliders);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const handleNextToConfig = () => {
        const onlyVoiceAgent = selectedIds.every(id => {
            const addon = addonsData.find(a => a.id === id);
            return addon.metadata?.name === 'AI_VOICE_AGENT';
        });
        if (onlyVoiceAgent) {
            handleNextToPayment();
        } else {
            setStep(2);
        }
    };

    const handleNextToPayment = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(
                `${API_SERVICE_BACKEND}/getUserPaymentMethods?userEmail=${encodeURIComponent(userEmail)}`
            );
            const methods = res.data.paymentMethods || [];
            setPaymentMethods(methods);
            if (methods.length > 0) setSelectedPaymentMethod(methods[0].id);
            
            // Clear coupon state when moving forward from configuration (quantities might have changed)
            setCouponCode('');
            setIsCouponApplied(false);
            setPreviewData(null);
            
            setStep(3);
        } catch {
            toast.error('Failed to fetch payment methods.');
            setStep(3);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNextToPreview = async () => {
        if (!selectedPaymentMethod) {
            toast.error('Please select a payment method');
            return;
        }
        
        setIsLoading(true);
        try {
            // Fetch preview with coupon if it was previously applied
            if (isCouponApplied && couponCode) {
                try {
                    await fetchPreviewData(couponCode);
                } catch (couponErr) {
                    // If coupon fails (e.g., invalid after quantity change), reset it and fetch without coupon
                    console.error('Coupon validation failed:', couponErr);
                    setCouponCode('');
                    setIsCouponApplied(false);
                    toast.error('Coupon is no longer valid. Please try again.');
                    await fetchPreviewData();
                }
            } else {
                await fetchPreviewData();
            }
            setStep(4);
        } catch (err) {
            toast.error('Failed to load preview. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPreviewData = async (coupon = null) => {
        try {
            const items = selectedIds.map(id => {
                const addon = addonsData.find(a => a.id === id);
                const addonName = addon.metadata?.name;
                const isMultichannel = addonName === 'MULTICHANNEL_ACCOUNTS';
                const isVoiceMins = addonName === 'AI_VOICE_AGENT_MINS';
                const isPhoneNumbers = addonName === 'PHONE_NUMBERS';
                const isLeads = addonName === 'LEADS';
                const isEmailValidation = addonName === 'EMAIL_VALIDATION';
                const isAi = addonName === 'AI';
                const isAiKnowledgeBasePages = addonName === 'AI_KNOWLEDGE_BASE_PAGES';
                const isUserMailboxes = addonName === 'USER_MAILBOXES';
                const sliderValue = sliders[id];
                const quantity = (isVoiceMins || isPhoneNumbers || isLeads || isEmailValidation || isAi || isMultichannel || isAiKnowledgeBasePages || isUserMailboxes) ? sliderValue : 1;

                return {
                    priceId: addon.default_price.id,
                    quantity,
                    addOnType: addonName
                };
            });

            const payload = {
                email: userEmail,
                items
            };

            if (coupon) {
                payload.couponCode = coupon;
            }

            const res = await axios.post(
                `${API_SERVICE_BACKEND}/previewAddOnSubscription`,
                payload
            );

            setPreviewData(res.data);
            return res.data;
        } catch (err) {
            console.error('Error fetching preview:', err);
            throw err;
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            toast.error('Please enter a coupon code');
            return;
        }

        setIsApplyingCoupon(true);
        try {
            const res = await fetchPreviewData(couponCode);
            if (res.error) {
                setCouponCode('');
                toast.error(res.error)
            } else {
                setIsCouponApplied(true);
                toast.success('Coupon applied successfully!');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Invalid coupon code');
            setIsCouponApplied(false);
            setPreviewData(null);
            // Fetch preview without coupon to restore original pricing
            await fetchPreviewData();
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = async () => {
        setCouponCode('');
        setIsCouponApplied(false);
        try {
            setIsApplyingCoupon(true);
            await fetchPreviewData();
        } catch (err) {
            console.error('Error resetting preview:', err);
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const handleCouponTryAgain = async () => {
        setCouponCode('');
        setIsCouponApplied(false);
        setPreviewData({})
    };

    const handleAddCard = async () => {
        setIsLoadingCard(true);
        try {
            const res = await axios.post(
                `${API_SERVICE_BACKEND}/payment-method`,
                { userEmail, isDfyPage: false }
            );
            if (res.data.url) {
                window.open(res.data.url, '_blank');
                toast.success('Redirected to add card.');
                handleClose();
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to add card.');
        } finally {
            setIsLoadingCard(false);
        }
    };

    const handleConfirmSubscription = async () => {
        setIsConfirming(true);
        try {
            await Promise.all(selectedIds.map(async (id) => {
                const addon = addonsData.find(a => a.id === id);
                const addonName = addon.metadata?.name;
                const isMultichannel = addonName === 'MULTICHANNEL_ACCOUNTS';
                const isVoiceMins = addonName === 'AI_VOICE_AGENT_MINS';
                const isPhoneNumbers = addonName === 'PHONE_NUMBERS';
                const isLeads = addonName === 'LEADS';
                const isEmailValidation = addonName === 'EMAIL_VALIDATION';
                const isAi = addonName === 'AI';
                const isAiKnowledgeBasePages = addonName === 'AI_KNOWLEDGE_BASE_PAGES';
                const isUserMailboxes = addonName === 'USER_MAILBOXES';
                const sliderValue = sliders[id];
                const quantity = (isVoiceMins || isPhoneNumbers || isLeads || isEmailValidation || isAi || isMultichannel || isAiKnowledgeBasePages || isUserMailboxes) ? sliderValue : 1;

                const payload = {
                    email: userEmail,
                    priceId: addon.default_price.id,
                    quantity,
                    paymentMethodId: selectedPaymentMethod,
                    addOnType: addonName
                };

                // Add coupon code if applied
                if (isCouponApplied && couponCode) {
                    payload.couponCode = couponCode;
                }

                const res = await axios.post(
                    `${API_SERVICE_BACKEND}/addon-subscription`,
                    payload
                );
                if (res?.data?.message) {
                    toast.success(res.data.message);
                } else {
                    toast.success('Add-on subscribed successfully!');
                }
            }));

            await getUserInfo(userEmail);
            handleClose();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to subscribe.');
        } finally {
            setIsConfirming(false);
        }
    };


    const renderStepContent = () => {
        if (step === 1) {
            return (
                <>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Enhance your plan with Add-ons.
                    </Typography>
                    {addonsData?.map(addon => {
                        const name = addon.metadata?.name;
                        const match = userDetailsRecurring.find(sub => sub.planId === name && !sub.cancelled);
                        const disabled = name === 'AI_VOICE_AGENT' && match;
                        const selected = selectedIds.includes(addon.id);
                        const creditsMeta = {
                            'MULTICHANNEL_ACCOUNTS': "per account",
                            'AI_VOICE_AGENT_MINS': "per 300 mins",
                            'LEADS': "per 5,000 leads",
                            'EMAIL_VALIDATION': "per 2,500 validations",
                            'AI': "per 1,000 credits",
                            'AI_KNOWLEDGE_BASE_PAGES': "per 1,000 pages",
                            'USER_MAILBOXES': "per mailbox",
                        }

                        return (
                            <Card
                                key={addon.id}
                                variant="outlined"
                                onClick={() => !disabled && handleToggleSelect(addon.id)}
                                sx={{
                                    p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    borderWidth: selected ? 2 : 1,
                                    opacity: disabled ? 0.4 : 1,
                                    borderRadius: 2,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                                className="glass-wrapper-50"
                            >
                                <Checkbox checked={selected} disabled={disabled} sx={{ mr: 2 }} />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography fontWeight="bold">{addon.name}</Typography>
                                    {disabled && (
                                        <Typography variant="caption" color="error">Already subscribed</Typography>
                                    )}
                                </Box>
                                <Typography variant="body1" fontWeight="bold">
                                    ${(addon.default_price?.unit_amount / 100).toLocaleString()}/month {creditsMeta[addon.metadata?.name]}
                                </Typography>
                            </Card>
                        );
                    })}
                </>
            );
        }

        if (step === 2) {
            return (
                <>
                    <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                        <IconButton onClick={() => setStep(1)} size="small">
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography variant="h6">Configure Add-ons</Typography>
                    </Stack>
                    {selectedIds.map(id => {
                        const addon = addonsData.find(a => a.id === id);
                        const addonName = addon.metadata?.name;
                        const isMultichannel = addonName === 'MULTICHANNEL_ACCOUNTS';
                        const isVoiceMins = addonName === 'AI_VOICE_AGENT_MINS';
                        const isLeads = addonName === 'LEADS';
                        const isVoice = addonName === 'AI_VOICE_AGENT';
                        const isPhoneNumbers = addonName === 'PHONE_NUMBERS';
                        const isEmailValidation = addonName === 'EMAIL_VALIDATION';
                        const isAiKnowledgeBasePages = addonName === 'AI_KNOWLEDGE_BASE_PAGES';
                        const isUserMailboxes = addonName === 'USER_MAILBOXES';
                        const isAi = addonName === 'AI';

                        const chunkSize = addon?.default_price?.transform_quantity?.divide_by
                            ? parseInt(addon.default_price?.transform_quantity?.divide_by)
                            : 1;

                        const min = isMultichannel ? 1 : chunkSize;
                        const max = isVoiceMins ? 3000 : isLeads || isEmailValidation || isAi || isAiKnowledgeBasePages ? 40000 : (isMultichannel || isPhoneNumbers) ? 40 : isUserMailboxes ? 500 : chunkSize * 10;

                        const sliderValue = sliders[id] ?? min;

                        const unitAmount = parseInt(addon?.default_price?.unit_amount || 0);
                        const quantity = (isVoiceMins || isPhoneNumbers || isLeads || isEmailValidation || isAi || isMultichannel || isAiKnowledgeBasePages || isUserMailboxes) ? sliderValue : 1;
                        let cost = ((quantity / chunkSize) * (unitAmount / 100)).toLocaleString(undefined, { minimumFractionDigits: 2 });

                        return (
                            <Box key={id} p={2} mb={2} border="1px solid #ddd" borderRadius={2}>
                                <Typography fontWeight="bold">{addon.name}</Typography>
                                {!isVoice && <Slider
                                    value={sliderValue}
                                    onChange={(_, val) => setSliders({ ...sliders, [id]: val })}
                                    step={chunkSize}
                                    min={min}
                                    max={max}
                                    sx={{ mb: 1 }}
                                />}
                                <Typography>Selected: <strong>{sliderValue.toLocaleString()}</strong></Typography>
                                <Typography>Total: <strong>${cost}</strong></Typography>
                            </Box>
                        );
                    })}
                </>
            );
        }

        if (step === 3) {
            const onlyVoiceAgent = selectedIds.every(id => {
                const addon = addonsData.find(a => a.id === id);
                return addon.metadata?.name === 'AI_VOICE_AGENT';
            });

            return (
                <>
                    <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                        <IconButton onClick={() => {
                            const targetStep = onlyVoiceAgent ? 1 : 2;
                            setStep(targetStep);
                            // Reset coupon if going back to configuration
                            if (targetStep === 2) {
                                setCouponCode('');
                                setIsCouponApplied(false);
                                setPreviewData(null);
                            }
                        }} size="small">
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography variant="h6">Select Payment Method</Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Please select a payment method to continue.
                    </Typography>

                    {paymentMethods.length > 0 ? (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Payment Method
                            </Typography>
                            <Select
                                fullWidth
                                 className='glass-dropdown'
                                value={selectedPaymentMethod}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                            >
                                {paymentMethods.map(pm => {
                                    if (pm.type === 'card' && pm.card) {
                                        return (
                                            <MenuItem key={pm.id} value={pm.id}>
                                                {pm.card?.brand?.toUpperCase()} ****{pm.card?.last4}
                                            </MenuItem>
                                        );
                                    } else if (pm.type === 'link' && pm.link) {
                                        return (
                                            <MenuItem key={pm.id} value={pm.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <img
                                                    src="/assets/link.svg"
                                                    alt="Link Logo"
                                                    width={35}
                                                />
                                                {" "}{" "}{" "}
                                                <span>
                                                    {pm.link?.email || 'Link Account'}
                                                </span>
                                            </MenuItem>
                                        );
                                    } else {
                                        return (
                                            <MenuItem key={pm.id} value={pm.id}>
                                                {pm.type?.toUpperCase()} {pm.id}
                                            </MenuItem>
                                        );
                                    }
                                })}
                            </Select>
                        </Box>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }} className='glass-pane'>
                            No saved payment methods found. Please add a payment method to continue.
                        </Alert>
                    )}
                </>
            );
        }

        if (step === 4) {
            // Use summary data from API response
            const summary = previewData?.summary || {};
            const displaySubtotal = summary.subtotal ? (summary.subtotal / 100).toFixed(2) : '0.00';
            const displayDiscount = summary.totalDiscount ? (summary.totalDiscount / 100).toFixed(2) : '0.00';
            const displayTotal = summary.total ? (summary.total / 100).toFixed(2) : '0.00';
            
            // Calculate scheduled amount from downgrade items (if any)
            const scheduledAmount = previewData?.items?.reduce((sum, item) => {
                if (item.changeType === 'downgrade') {
                    return sum + (item.total || item.subtotal || 0);
                }
                return sum;
            }, 0) || 0;
            const displayScheduledAmount = scheduledAmount > 0 ? (scheduledAmount / 100).toFixed(2) : '0.00';

            return (
                <>
                    <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                        <IconButton onClick={() => setStep(3)} size="small">
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography variant="h6">Review & Confirm</Typography>
                    </Stack>

                    {/* Downgrade Info Alert */}
                    {previewData?.downgradeInfo && (
                        <Alert 
                            severity="info" 
                            sx={{ mb: 3 }}
                            icon={<TrendingDown />}
                        >
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                Scheduled Changes
                            </Typography>
                            <Typography variant="body2">
                                {previewData.downgradeInfo.message}
                            </Typography>
                        </Alert>
                    )}

                    {/* Items List */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Add-ons Summary
                        </Typography>
                        {previewData?.items ? (
                            previewData.items.map((item, index) => {
                                const addon = addonsData.find(a => a.default_price.id === item.priceId);
                                const changeType = item.changeType || 'new';
                                const isDowngrade = changeType === 'downgrade';
                                const isUpgrade = changeType === 'upgrade';
                                const isNew = changeType === 'new';
                                const isNoChange = changeType === 'no_change';
                                
                                // Get change type badge color and icon
                                const getChangeTypeBadge = () => {
                                    switch (changeType) {
                                        case 'new':
                                            return { color: 'success', label: 'New Purchase', icon: <Add fontSize="small" /> };
                                        case 'upgrade':
                                            return { color: 'info', label: 'Upgrade', icon: <TrendingUp fontSize="small" /> };
                                        case 'downgrade':
                                            return { color: 'warning', label: 'Scheduled Downgrade', icon: <TrendingDown fontSize="small" /> };
                                        case 'no_change':
                                            return { color: 'default', label: 'No Change', icon: null };
                                        default:
                                            return { color: 'default', label: changeType, icon: null };
                                    }
                                };

                                const badge = getChangeTypeBadge();

                                return (
                                    <Box 
                                        key={index} 
                                        p={2} 
                                        mb={1} 
                                        sx={{ 
                                            border: '1px solid',
                                            borderColor: isDowngrade ? 'warning.main' : 'divider',
                                            borderRadius: 2,
                                            bgcolor: isDowngrade 
                                                ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.1)
                                                : 'background.paper',
                                            opacity: isNoChange ? 0.7 : 1
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                                <Box sx={{ flex: 1 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                        <Typography fontWeight="bold">{addon?.name || item.product}</Typography>
                                                        <Chip 
                                                            label={badge.label}
                                                            color={badge.color}
                                                            size="small"
                                                            icon={badge.icon}
                                                            sx={{ 
                                                                height: 24, 
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600,
                                                                '& .MuiChip-icon': {
                                                                    color: '#ffffff',
                                                                    fontSize: '16px'
                                                                }
                                                            }}
                                                        />
                                                    </Stack>
                                                    
                                                    {/* Quantity change info */}
                                                    {(item.currentQuantity !== undefined && item.newQuantity !== undefined) && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {isUpgrade && (
                                                                <>Upgrading from <strong>{item.currentQuantity.toLocaleString()}</strong> to <strong>{item.newQuantity.toLocaleString()}</strong></>
                                                            )}
                                                            {isDowngrade && (
                                                                <>Downgrading from <strong>{item.currentQuantity.toLocaleString()}</strong> to <strong>{item.newQuantity.toLocaleString()}</strong></>
                                                            )}
                                                            {isNoChange && (
                                                                <>Quantity: <strong>{item.newQuantity.toLocaleString()}</strong> (unchanged)</>
                                                            )}
                                                            {isNew && (
                                                                <>Quantity: <strong>{item.newQuantity.toLocaleString()}</strong></>
                                                            )}
                                                        </Typography>
                                                    )}

                                                    {/* Billing Cycle */}
                                                    {item.billingPeriod && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            Billing Cycle: <strong>{formatToLocalDate(item.billingPeriod.start * 1000)}</strong> - <strong>{formatToLocalDate(item.billingPeriod.end * 1000)}</strong>
                                                        </Typography>
                                                    )}

                                                    {/* Effective date for downgrades */}
                                                    {isDowngrade && item.effectiveDate && (
                                                        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                                                            <Typography variant="caption">
                                                                Effective: {formatToLocalDate(new Date(item.effectiveDate).getTime())}
                                                            </Typography>
                                                        </Alert>
                                                    )}

                                                    {/* Change type specific messages */}
                                                    {isDowngrade && (
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                display: 'block', 
                                                                mt: 0.5,
                                                                color: theme.palette.mode === 'dark' 
                                                                    ? theme.palette.warning.light 
                                                                    : theme.palette.warning.dark
                                                            }}
                                                        >
                                                            ${(item.total / 100).toFixed(2)} in scheduled changes will take effect at the end of your billing cycle. No charge now.
                                                        </Typography>
                                                    )}
                                                    {isUpgrade && (
                                                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                                                            Upgrade will be applied immediately.
                                                        </Typography>
                                                    )}
                                                    {isNew && (
                                                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                                            New subscription will start immediately.
                                                        </Typography>
                                                    )}
                                                    {isNoChange && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                            No changes to your current subscription.
                                                        </Typography>
                                                    )}

                                                    {!isNoChange && item.discount?.applied && (
                                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                                            <CheckCircle color="success" sx={{ fontSize: 16 }} />
                                                            <Typography variant="caption" color="success.main" fontWeight={600}>
                                                                Discount applied
                                                                {item.discount.couponName && ` (${item.discount.couponName})`}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Box>
                                                <Box textAlign="right" sx={{ ml: 2 }}>
                                                    {isDowngrade ? (
                                                        <Stack alignItems="flex-end">
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                                            >
                                                                ${(item.subtotal / 100).toFixed(2)}
                                                            </Typography>
                                                            <Typography 
                                                                fontWeight="bold" 
                                                                sx={{
                                                                    color: theme.palette.mode === 'dark' 
                                                                        ? theme.palette.warning.light 
                                                                        : theme.palette.warning.dark
                                                                }}
                                                            >
                                                                $0.00
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                (Scheduled)
                                                            </Typography>
                                                        </Stack>
                                                    ) : item.discount?.applied && item.discount.amount > 0 ? (
                                                        <>
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                                            >
                                                                ${(item.subtotal / 100).toFixed(2)}
                                                            </Typography>
                                                            <Typography fontWeight="bold" color="success.main">
                                                                ${((item.subtotal - item.discount.amount) / 100).toFixed(2)}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography fontWeight="bold">
                                                            ${(item.subtotal / 100).toFixed(2)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                );
                            })
                        ) : (
                            selectedIds.map(id => {
                                const addon = addonsData.find(a => a.id === id);
                                const addonName = addon.metadata?.name;
                                const isMultichannel = addonName === 'MULTICHANNEL_ACCOUNTS';
                                const isVoiceMins = addonName === 'AI_VOICE_AGENT_MINS';
                                const isPhoneNumbers = addonName === 'PHONE_NUMBERS';
                                const isLeads = addonName === 'LEADS';
                                const isEmailValidation = addonName === 'EMAIL_VALIDATION';
                                const isAi = addonName === 'AI';
                                const isAiKnowledgeBasePages = addonName === 'AI_KNOWLEDGE_BASE_PAGES';
                                const isUserMailboxes = addonName === 'USER_MAILBOXES';
                                const chunkSize = addon?.default_price?.transform_quantity?.divide_by
                                    ? parseInt(addon?.default_price?.transform_quantity?.divide_by)
                                    : 1;
                                const sliderValue = sliders[id];
                                const quantity = (isVoiceMins || isPhoneNumbers || isLeads || isEmailValidation || isAi || isMultichannel || isAiKnowledgeBasePages || isUserMailboxes) ? sliderValue : 1;
                                const unitAmount = parseInt(addon?.default_price?.unit_amount || 0);
                                let cost = ((quantity / chunkSize) * (unitAmount / 100)).toFixed(2);
                                return (
                                    <Box 
                                        key={id} 
                                        p={2} 
                                        mb={1} 
                                        sx={{ 
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2 
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between">
                                            <Box>
                                                <Typography fontWeight="bold">{addon.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Quantity: {quantity.toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Typography fontWeight="bold">${cost}</Typography>
                                        </Stack>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* Coupon Section */}
                    <Box sx={{ mb: 3 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Coupon Code
                        </Typography>
                        
                        {isCouponApplied ? (
                            <Alert 
                                severity="success" 
                                action={
                                    <Button color="inherit" size="small" onClick={handleRemoveCoupon}>
                                        Remove
                                    </Button>
                                }
                            >
                                Coupon "{couponCode}" applied successfully!
                            </Alert>
                        ) : previewData?.error ? (<Alert
                            severity="error"
                            action={
                                <Button color="inherit" size="small" onClick={handleCouponTryAgain}>
                                    Try again
                                </Button>
                            }
                        >
                            {previewData?.error}
                        </Alert>) : (
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    placeholder="Enter coupon code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    fullWidth
                                    disabled={isApplyingCoupon}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                          padding: '12px 12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                        },
                                      }}
                                />
                                <Button 
                                    variant="outlined" 
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode.trim() || isApplyingCoupon}
                                    sx={{ minWidth: 100 }}
                                >
                                    {isApplyingCoupon ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        'Apply'
                                    )}
                                </Button>
                            </Stack>
                        )}
                    </Box>

                    {/* Pricing Summary */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.neutral', borderRadius: 2 }}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography>Subtotal:</Typography>
                                <Typography>${displaySubtotal}</Typography>
                            </Stack>
                            {parseFloat(displayDiscount) > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="success.main">Discount:</Typography>
                                    <Typography color="success.main" fontWeight="bold">
                                        -${displayDiscount}
                                    </Typography>
                                </Stack>
                            )}
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="h6" fontWeight="bold">Total Due Now:</Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary.main">
                                    ${displayTotal}
                                </Typography>
                            </Stack>
                            {/* {parseFloat(displayScheduledAmount) > 0 && (
                                <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                                    <Typography variant="caption">
                                        ${displayScheduledAmount} in scheduled changes will take effect at the end of your billing cycle. No charge now.
                                    </Typography>
                                </Alert>
                            )}
                            {parseFloat(displayTotal) === '0.00' && parseFloat(displayScheduledAmount) > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    All changes are scheduled for the end of your billing cycle. No immediate charge.
                                </Typography>
                            )}
                            {parseFloat(displayTotal) > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    Billed monthly
                                </Typography>
                            )} */}
                        </Stack>
                    </Box>

                    {/* Selected Payment Method Display */}
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Payment Method
                        </Typography>
                        {selectedPaymentMethod && paymentMethods.length > 0 ? (
                            <Box 
                                sx={{ 
                                    p: 2, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                {(() => {
                                    const pm = paymentMethods.find(p => p.id === selectedPaymentMethod);
                                    if (pm?.type === 'card' && pm.card) {
                                        return (
                                            <Typography fontWeight="bold">
                                                {pm.card?.brand?.toUpperCase()} ****{pm.card?.last4}
                                            </Typography>
                                        );
                                    } else if (pm?.type === 'link' && pm.link) {
                                        return (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <img
                                                    src="/assets/link.svg"
                                                    alt="Link Logo"
                                                    width={35}
                                                />
                                                <Typography fontWeight="bold">
                                                    {pm.link?.email || 'Link Account'}
                                                </Typography>
                                            </Stack>
                                        );
                                    } else {
                                        return (
                                            <Typography fontWeight="bold">
                                                {pm?.type?.toUpperCase()}
                                            </Typography>
                                        );
                                    }
                                })()}
                                <Button 
                                    size="small" 
                                    onClick={() => setStep(3)}
                                    sx={{ mt: 1 }}
                                >
                                    Change Payment Method
                                </Button>
                            </Box>
                        ) : (
                            <Alert severity="error">No payment method selected.</Alert>
                        )}
                    </Box>
                </>
            );
        }

        return null;
    };

    const renderActions = () => {
        if (step === 1) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        width: "100%",
                    }}
                >

                    {/* <Box
                        sx={{
                            textAlign: "left",
                            ml: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: (theme) => theme.palette.text.secondary,
                                lineHeight: "18px",
                                display: "inline",
                            }}
                        >
                            Need Help?{" "}
                            <Link
                                href={`${HELP_URL}/11882347-usage-features-monitor-manage#h_142aab9896`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    lineHeight: "18px",
                                    color: (theme) => theme.palette.primary.main,
                                    textDecoration: "none",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                    display: "inline",
                                }}
                            >
                                Learn how Add-ons work
                            </Link>
                        </Typography>
                    </Box> */}
                    <HelperLink
                        helperText={"Need Help? "}
                        actionText={"Learn how Add-ons work"}
                        style={{
                            textAlign: "left",
                            ml: 1,
                        }}
                    />


                    <Button variant="contained" disabled={selectedIds.length === 0} onClick={handleNextToConfig}>
                        Continue
                    </Button>
                </Box>
            );
        }
        if (step === 2) {
            return (
                <Button variant="contained" onClick={handleNextToPayment} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} /> Continue
                        </>
                    ) : 'Continue'}
                </Button>
            );
        }
        if (step === 3) {
            return (
                <Box display="flex" justifyContent="space-between" width="100%">
                    <Button variant="outlined" onClick={handleAddCard} disabled={isLoadingCard || isLoading} className='glass-button-secondary-50'>
                        {isLoadingCard ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} /> Add New Card
                            </>
                        ) : 'Add New Card'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleNextToPreview}
                        disabled={!selectedPaymentMethod || isLoading || isLoadingCard}
                    >
                        {isLoading ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} /> Continue
                            </>
                        ) : 'Continue'}
                    </Button>
                </Box>
            );
        }
        if (step === 4) {
            return (
                <Button
                    variant="contained"
                    onClick={handleConfirmSubscription}
                    disabled={!selectedPaymentMethod || isConfirming || isApplyingCoupon}
                    fullWidth
                >
                    {isConfirming ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} /> Confirming...
                        </>
                    ) : 'Confirm Subscription'}
                </Button>
            );
        }
        return null;
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={handleOpen}
                disabled={userDetails?.hasTeamMemberLimitedAccess}
            >
                Manage Addon
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Purchase Add-ons</DialogTitle>
                <DialogContent>{renderStepContent()}</DialogContent>
                <DialogActions>{renderActions()}</DialogActions>
            </Dialog>
        </>
    );
};

export default AddonModal;
